"use client";

import * as React from "react";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {useAppSelector} from "@/lib/hooks";
import {selectVisibleDialogs} from "@/lib/slices/dialogsSlice";
import {getOrchestrator} from "../singleton";
import {VisibleDialog} from "../types";
import {DIALOG_COMPONENTS} from "./registry";
import {useDialogController} from "./hooks";

/**
 * Renders the orchestrator's visible-dialog set. Mount once near the root.
 *
 * It is intentionally thin: it reads the mirrored list from Redux, resolves each
 * id to a component, and feeds it the controller props. All ordering/visibility
 * decisions already happened in the engine, so there are no effects or
 * conditionals about *which* dialog wins here.
 */
export function DialogHost() {
    const visible = useAppSelector(selectVisibleDialogs);
    return (
        <>
            {visible.map((entry) => (
                <DialogSlot key={entry.instanceId} entry={entry} />
            ))}
        </>
    );
}

function DialogSlot({entry}: {entry: VisibleDialog}) {
    const Component = DIALOG_COMPONENTS[entry.id] as
        | React.ComponentType<Record<string, unknown>>
        | undefined;
    const controller = useDialogController(entry.instanceId);

    if (!Component) {
        if (process.env.NODE_ENV !== "production") {
             
            console.warn(`[dialog-orchestrator] no component registered for "${entry.id}"`);
        }
        return null;
    }

    // Live props (incl. callbacks) are kept in the engine, not Redux. The mirror
    // is flushed a microtask after the engine changes (AC-817), so right after a
    // close it can still list an instance the engine already dropped. Rendering
    // it then would hand the dialog `{}` instead of its props (ALDEX-CASINO-93).
    if (!getOrchestrator().isActive(entry.instanceId)) return null;
    const props = getOrchestrator().getProps(entry.instanceId) ?? {};

    return (
        <Component
            {...props}
            open={controller.open}
            onOpenChange={controller.onOpenChange}
            orchestratorResolve={controller.resolve}
            orchestratorDismiss={controller.dismiss}
            orchestratorZIndex={entry.zIndex}
        />
    );
}

/** Re-exported for convenience so callers can `import {DialogsEnum}` from here. */
export {DialogsEnum};
